// ─────────────────────────────────────────────────────────────────────────────
// graphStore — the authoritative graph, and the indexes the renderer reads.
//
// THE STORE IS COMPLETE; VISIBILITY IS SEMANTIC ZOOM, NOT DELETION. Nothing is
// dropped from here because the camera is far away — the renderer decides how
// much of it to resolve. That separation is what stops "zoom out" from silently
// becoming "lose data".
//
// EVERY MUTATION IS INCREMENTAL. Adding a fourth Personal agent touches that
// agent, its edge and the two index entries they belong to — never a rebuild of
// the whole store. The patch path is the ONLY way data changes, so the G1 live
// patch test exercises exactly the code a real update will use.
//
// No for..of / no spread over Maps and Sets anywhere in this module: the app
// compiles at target es5 without downlevelIteration, and iterating a Map that
// way is a compile error rather than a runtime surprise.
// ─────────────────────────────────────────────────────────────────────────────

import {
  RexyGraphEdgeRecord,
  RexyGraphNodeKind,
  RexyGraphNodeRecord,
  RexyGraphPatch,
  RexyGraphSnapshot,
  RexyTrustTier,
} from './graphTypes';

type IdList = string[];

function pushUnique(list: IdList, id: string): void {
  if (list.indexOf(id) < 0) list.push(id);
}

function removeFrom(list: IdList | undefined, id: string): void {
  if (!list) return;
  const i = list.indexOf(id);
  if (i >= 0) list.splice(i, 1);
}

function bucket(map: Map<string, IdList>, key: string): IdList {
  let list = map.get(key);
  if (!list) {
    list = [];
    map.set(key, list);
  }
  return list;
}

export class RexyGraphStore {
  readonly nodes = new Map<string, RexyGraphNodeRecord>();
  readonly edges = new Map<string, RexyGraphEdgeRecord>();

  readonly childrenByParentId = new Map<string, IdList>();
  readonly edgesByNodeId = new Map<string, IdList>();
  readonly nodesByDomainId = new Map<string, IdList>();
  readonly nodesByAgentId = new Map<string, IdList>();
  readonly nodesByKind = new Map<string, IdList>();
  readonly nodesByTrustTier = new Map<string, IdList>();

  /** Source revision of the last applied snapshot/patch. */
  revision: string | number = 0;

  /** Bumped on every mutation. The React layer watches this, not the Maps. */
  version = 0;

  private listeners: Array<() => void> = [];

  onChange(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      const i = this.listeners.indexOf(listener);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  private emit(): void {
    this.version++;
    const ls = this.listeners;
    for (let i = 0; i < ls.length; i++) ls[i]();
  }

  // ── reads ──────────────────────────────────────────────────────────────────

  get nodeCount(): number {
    return this.nodes.size;
  }

  get edgeCount(): number {
    return this.edges.size;
  }

  getNode(id: string): RexyGraphNodeRecord | undefined {
    return this.nodes.get(id);
  }

  getEdge(id: string): RexyGraphEdgeRecord | undefined {
    return this.edges.get(id);
  }

  childrenOf(parentId: string): IdList {
    return this.childrenByParentId.get(parentId) || [];
  }

  edgesOf(nodeId: string): IdList {
    return this.edgesByNodeId.get(nodeId) || [];
  }

  nodesOfKind(kind: RexyGraphNodeKind): IdList {
    return this.nodesByKind.get(kind) || [];
  }

  nodesOfDomain(domainId: string): IdList {
    return this.nodesByDomainId.get(domainId) || [];
  }

  nodesOfAgent(agentId: string): IdList {
    return this.nodesByAgentId.get(agentId) || [];
  }

  nodesOfTrust(tier: RexyTrustTier): IdList {
    return this.nodesByTrustTier.get(tier) || [];
  }

  /** Map.forEach — never a for..of. See the module header. */
  forEachNode(fn: (node: RexyGraphNodeRecord) => void): void {
    this.nodes.forEach(fn);
  }

  forEachEdge(fn: (edge: RexyGraphEdgeRecord) => void): void {
    this.edges.forEach(fn);
  }

  // ── writes ─────────────────────────────────────────────────────────────────

  applySnapshot(snapshot: RexyGraphSnapshot): void {
    this.nodes.clear();
    this.edges.clear();
    this.childrenByParentId.clear();
    this.edgesByNodeId.clear();
    this.nodesByDomainId.clear();
    this.nodesByAgentId.clear();
    this.nodesByKind.clear();
    this.nodesByTrustTier.clear();

    for (let i = 0; i < snapshot.nodes.length; i++) this.indexNode(snapshot.nodes[i]);
    for (let i = 0; i < snapshot.edges.length; i++) this.indexEdge(snapshot.edges[i]);

    this.revision = snapshot.revision;
    this.emit();
  }

  applyPatch(patch: RexyGraphPatch): void {
    const removeNodes = patch.removeNodeIds;
    if (removeNodes) for (let i = 0; i < removeNodes.length; i++) this.removeNode(removeNodes[i]);

    const removeEdges = patch.removeEdgeIds;
    if (removeEdges) for (let i = 0; i < removeEdges.length; i++) this.removeEdge(removeEdges[i]);

    const upsertNodes = patch.upsertNodes;
    if (upsertNodes) for (let i = 0; i < upsertNodes.length; i++) this.indexNode(upsertNodes[i]);

    const upsertEdges = patch.upsertEdges;
    if (upsertEdges) for (let i = 0; i < upsertEdges.length; i++) this.indexEdge(upsertEdges[i]);

    this.revision = patch.revision;
    this.emit();
  }

  // ── indexing ───────────────────────────────────────────────────────────────
  // An upsert of an existing id de-indexes the OLD record before indexing the
  // new one. Without that, re-parenting or re-tiering a node would leave it in
  // both buckets and the stale one would quietly out-live it.

  private indexNode(node: RexyGraphNodeRecord): void {
    const prior = this.nodes.get(node.id);
    if (prior) this.deindexNode(prior);

    this.nodes.set(node.id, node);
    if (node.parentId) pushUnique(bucket(this.childrenByParentId, node.parentId), node.id);
    if (node.domainId) pushUnique(bucket(this.nodesByDomainId, node.domainId), node.id);
    if (node.agentId) pushUnique(bucket(this.nodesByAgentId, node.agentId), node.id);
    pushUnique(bucket(this.nodesByKind, node.kind), node.id);
    if (node.trustTier) pushUnique(bucket(this.nodesByTrustTier, node.trustTier), node.id);
  }

  private deindexNode(node: RexyGraphNodeRecord): void {
    if (node.parentId) removeFrom(this.childrenByParentId.get(node.parentId), node.id);
    if (node.domainId) removeFrom(this.nodesByDomainId.get(node.domainId), node.id);
    if (node.agentId) removeFrom(this.nodesByAgentId.get(node.agentId), node.id);
    removeFrom(this.nodesByKind.get(node.kind), node.id);
    if (node.trustTier) removeFrom(this.nodesByTrustTier.get(node.trustTier), node.id);
  }

  private indexEdge(edge: RexyGraphEdgeRecord): void {
    const prior = this.edges.get(edge.id);
    if (prior) this.deindexEdge(prior);

    this.edges.set(edge.id, edge);
    pushUnique(bucket(this.edgesByNodeId, edge.sourceId), edge.id);
    pushUnique(bucket(this.edgesByNodeId, edge.targetId), edge.id);
  }

  private deindexEdge(edge: RexyGraphEdgeRecord): void {
    removeFrom(this.edgesByNodeId.get(edge.sourceId), edge.id);
    removeFrom(this.edgesByNodeId.get(edge.targetId), edge.id);
  }

  /**
   * Removing a node removes the edges that touch it. An edge whose endpoint is
   * gone is not a faint line — it is a line to nowhere, and the buffer builder
   * would have to invent a position for it.
   */
  private removeNode(id: string): void {
    const node = this.nodes.get(id);
    if (!node) return;

    const touching = this.edgesByNodeId.get(id);
    if (touching) {
      const copy = touching.slice();
      for (let i = 0; i < copy.length; i++) this.removeEdge(copy[i]);
    }

    this.deindexNode(node);
    this.edgesByNodeId.delete(id);
    this.childrenByParentId.delete(id);
    this.nodes.delete(id);
  }

  private removeEdge(id: string): void {
    const edge = this.edges.get(id);
    if (!edge) return;
    this.deindexEdge(edge);
    this.edges.delete(id);
  }
}
